/**
 * Lightweight domain-quality heuristics.
 *
 * These run entirely on the client and are used to surface helpful hints in
 * the review/drill-in views — they are NOT authoritative availability signals.
 */

export type LengthScore = 'good' | 'okay' | 'poor';

export interface DomainScore {
  /** Qualitative length rating for the base name. */
  lengthScore: LengthScore;
  /** Whether the base name contains one or more hyphens. */
  hasHyphens: boolean;
  /** Whether the base name contains one or more digits. */
  hasNumbers: boolean;
  /** Character length of the base name (without TLD). */
  length: number;
}

/**
 * Extract the base name from a full domain.
 *
 * For `foo.co.uk` this returns `foo` — we simply take everything before the
 * first dot.  This is intentionally simplistic; a proper public-suffix check
 * is overkill for a quality-hint feature.
 */
function baseName(domain: string): string {
  const idx = domain.indexOf('.');
  return idx === -1 ? domain : domain.slice(0, idx);
}

/**
 * Classify a base-name length into good / okay / poor.
 *
 * - **good:** 5 -- 10 characters
 * - **okay:** 3 -- 4 or 11 -- 15 characters
 * - **poor:** 1 -- 2 or 16+ characters
 */
function classifyLength(len: number): LengthScore {
  if (len >= 5 && len <= 10) return 'good';
  if ((len >= 3 && len <= 4) || (len >= 11 && len <= 15)) return 'okay';
  return 'poor';
}

/**
 * Score a domain on several quick-glance quality signals.
 */
export function scoreDomain(domain: string): DomainScore {
  const base = baseName(domain);
  return {
    lengthScore: classifyLength(base.length),
    hasHyphens: base.includes('-'),
    hasNumbers: /\d/.test(base),
    length: base.length,
  };
}

/**
 * Find domains in `allDomains` that are similar to `domain`.
 *
 * Two domains are considered similar when:
 * 1. They share the same base name (ignoring TLD), OR
 * 2. One base name is a substring of the other.
 *
 * The target `domain` itself is excluded from the results.
 */
export function findSimilarDomains(
  domain: string,
  allDomains: string[],
): string[] {
  const targetBase = baseName(domain);
  if (targetBase.length === 0) return [];

  const matches: string[] = [];

  for (const candidate of allDomains) {
    if (candidate === domain) continue;

    const candidateBase = baseName(candidate);
    if (candidateBase.length === 0) continue;

    // Same base name across different TLDs.
    if (candidateBase === targetBase) {
      matches.push(candidate);
      continue;
    }

    // Substring match (either direction).
    if (
      candidateBase.includes(targetBase) ||
      targetBase.includes(candidateBase)
    ) {
      matches.push(candidate);
    }
  }

  return matches;
}
